/**
 * stores/ui.ts — Zustand UI store.
 *
 * Cross-screen UI-only state (theme, sidebar, modal stack). One of two stores
 * permitted for v1 per frontend-conventions.md §6.
 *
 * Does NOT hold server data, form values, or anything per-screen.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { applyTheme, getSystemTheme, isThemeName, type ThemeName } from '@/lib/theme';

interface UiState {
  // Theme — explicit user choice, or null for "follow system"
  theme: ThemeName | null;
  setTheme: (theme: ThemeName | null) => void;
  effectiveTheme: () => ThemeName;

  // Sidebar
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      theme: null,
      setTheme: (theme) => {
        const effective = theme ?? getSystemTheme();
        applyTheme(effective);
        set({ theme });
      },
      effectiveTheme: () => get().theme ?? getSystemTheme(),

      // Sidebar drawer state. Default closed; relevant only at <1024px
      // (mobile/tablet). At desktop the sidebar is always visible regardless.
      // Per-session only — not persisted; we don't want a sticky mobile
      // drawer following the user across devices.
      sidebarOpen: false,
      toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
    }),
    {
      name: 'linqage-ui',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ theme: state.theme }),
      onRehydrateStorage: () => (state) => {
        // Apply the rehydrated theme immediately so there's no flash.
        if (state) {
          const effective = isThemeName(state.theme) ? state.theme : getSystemTheme();
          applyTheme(effective);
        }
      },
    },
  ),
);
